import { getNeigong } from './data/neigong';
import { QI } from './data/qi';

/** กล้ามเนื้อ / สมดุล / ลมหายใจ / พลังจิต / ความอึด */
export interface Attributes {
  str: number;
  dex: number;
  ing: number;
  spi: number;
  sta: number;
}

export const BASE_ATTRIBUTES: Readonly<Attributes> = { str: 10, dex: 10, ing: 10, spi: 10, sta: 10 };

export const ATTRIBUTE_LABELS: Readonly<Record<keyof Attributes, string>> = {
  str: 'กล้ามเนื้อ',
  dex: 'สมดุล',
  ing: 'ลมหายใจ',
  spi: 'พลังจิต',
  sta: 'ความอึด',
};

/** What each attribute feeds, for tooltips. */
export const ATTRIBUTE_EFFECTS: Readonly<Record<keyof Attributes, string>> = {
  str: 'พลังระยะประชิด · พลังชีวิตสูงสุด · คริติคอลภายนอก',
  dex: 'แม่นยำภายนอก · หลบหลีก · พลังระยะไกล',
  ing: 'พลังโจมตีภายใน · ลมปราณสูงสุด',
  spi: 'แม่นยำภายใน · คริติคอลภายใน · ลมปราณสูงสุด · ป้องกันภายใน · ฟื้นฟูลมปราณ',
  sta: 'พลังชีวิตสูงสุด · ความแข็งแกร่ง · ฟื้นฟูพลังชีวิต',
};

export interface DerivedStats {
  maxHp: number;
  /** ลมปราณ (qi) pool. */
  maxQi: number;
  maxStamina: number;
  /** External (melee) power. */
  atk: number;
  /** Ranged power, for thrown-weapon and bow arts. */
  shotPower: number;
  /** Internal (inner-energy) power. */
  magic: number;
  /** Accuracy of external and internal attacks, against the defender's dodge. */
  hit: number;
  magicHit: number;
  dodge: number;
  /** 0–1 */
  crit: number;
  magicCrit: number;
  /** External and internal defence. */
  def: number;
  magicDef: number;
  /** Shortens stuns, as a percentage-like rating. */
  toughness: number;
  /** Qi and HP per second. */
  qiRegen: number;
  hpRegen: number;
  /** Tiles per second. */
  speed: number;
}

export function attributesFor(neigongId: string, level: number): Attributes {
  const n = getNeigong(neigongId);
  const a = { ...BASE_ATTRIBUTES };
  for (const k of Object.keys(a) as (keyof Attributes)[]) a[k] += (n.perLevel[k] ?? 0) * level;
  return a;
}

const r1 = (n: number) => Math.round(n * 10) / 10;

export function deriveStats(a: Attributes): DerivedStats {
  return {
    maxHp: Math.round(100 + a.str * 4 + a.sta * 8),
    maxQi: Math.round(QI.poolBase + (a.ing + a.spi) * QI.poolPerPoint),
    maxStamina: 100,
    atk: r1(6 + a.str * 1.2),
    shotPower: r1(6 + a.dex * 1.2),
    magic: r1(6 + a.ing * 1.2),
    hit: a.dex,
    magicHit: a.spi,
    dodge: a.dex,
    crit: Math.min(0.5, 0.05 + a.str * 0.004),
    magicCrit: Math.min(0.5, 0.05 + a.spi * 0.004),
    def: Math.round(a.sta * 1.5),
    magicDef: Math.round(a.spi * 1.5),
    toughness: r1(a.sta * 0.5),
    qiRegen: r1(QI.regenBase + a.spi * QI.regenPerSpi),
    hpRegen: r1(a.sta * 0.15),
    speed: Math.round((7.2 + a.dex * 0.02) * 100) / 100,
  };
}
